import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { openAlignmentWizard } from "@/modes/alignment-wizard/open-alignment-wizard";
import { isImageFileUploadTask } from "@/modes/create-area-mode/create-area-utils";
import {
  selectActiveBackgroundTasks,
  selectBackgroundTasks,
} from "@/store/background-tasks/background-tasks-selector";
import { setActiveCad } from "@/store/cad/cad-slice";
import { addIElementsToStore } from "@/store/ielements-store-utilities";
import { selectAreaFor } from "@/store/selections-selectors";
import { AppStore, store } from "@/store/store";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  BackgroundTask,
  BackgroundTaskTypes,
  getErrorCodeAndTaskName,
  isFileUploadTask,
  isMultiFileUploadTask,
  isPointCloudExportTask,
} from "@/utils/background-tasks";
import {
  CreateDialogProps,
  OpenToastOptions,
  selectAncestor,
  selectChildDepthFirst,
  selectIElement,
  TranslateVar,
} from "@faro-lotv/app-component-toolbox";
import { useDialog, useToast } from "@faro-lotv/flat-ui";
import { assert, getFileNameFromUrl } from "@faro-lotv/foundation";
import {
  isGeoReferencedElement,
  isIElementModel3dStream,
  isIElementPointCloudStream,
  isIElementSectionDataSession,
  isValidPose,
} from "@faro-lotv/ielement-types";
import {
  BackgroundTaskState,
  ProgressApiSupportedTaskTypes,
  ProjectApi,
  taskErrorToUserMessage,
} from "@faro-lotv/service-wires";
import { isEqual } from "es-toolkit";
import { useEffect, useState, type JSX } from "react";
import { updateProject } from "../common/project-provider/update-project";
import {
  selectDisableCaptureTreeAlignment,
  selectFromCaptureTree,
} from "./tree/cad-model-tree/use-disable-capture-tree-alignment";

/** Params to pass to a action function triggered when a task changes state */
type TaskActionParams = {
  /** The task that changed state */
  task: BackgroundTask;

  /** A function to open a toast */
  openToast(options: OpenToastOptions): unknown;

  /** A function to open a modal dialog */
  createDialog(options: CreateDialogProps): unknown;

  /** The redux store of the application */
  appStore: AppStore;

  /** Project API */
  projectApi: ProjectApi;
};

/** All the task actions possible for task state changes */
type TaskActions = {
  /** Action triggered when the task complete successfully */
  completed(params: TaskActionParams): Promise<void>;

  /** Action triggered when the task fail */
  failed(params: TaskActionParams): Promise<void>;
};

const pcConversionAction: TaskActions = {
  async completed({ task, openToast, appStore, projectApi }) {
    assert(
      task.type === ProgressApiSupportedTaskTypes.pointCloudLazToPotree ||
        task.type === ProgressApiSupportedTaskTypes.pointCloudToPotree,
    );

    const appState = appStore.getState();

    const element = selectIElement(task.iElementId)(appState);

    const cloudSection = selectAncestor(
      element,
      isIElementSectionDataSession,
    )(appState);

    const isGeoReferenced = isGeoReferencedElement(cloudSection);

    const isFromCaptureTree = selectFromCaptureTree(element)(appState);

    const disableAlignment =
      selectDisableCaptureTreeAlignment(element)(appState);

    // If the element is in the capture tree, retrieve the updated parent
    // and all the siblings
    if (isFromCaptureTree && element?.parentId) {
      const parent = (
        await projectApi.getAllIElements({
          ids: [element.parentId],
        })
      ).at(0);

      if (parent?.childrenIds) {
        await appStore.dispatch(
          updateProject({
            projectApi,
            iElementQuery: {
              ids: parent.childrenIds,
            },
          }),
        );

        addIElementsToStore([parent], appStore.dispatch);
      }
    }

    const message = (
      <>
        The import of{" "}
        {element?.name ? (
          <TranslateVar name="modelName">{element.name}</TranslateVar>
        ) : (
          "the point cloud"
        )}{" "}
        was completed successfully
      </>
    );

    openToast({
      title: "Import Successful",
      message,
      variant: "success",
      persist: true,
      action:
        disableAlignment ||
        isValidPose(cloudSection?.pose) ||
        isGeoReferenced ||
        isFromCaptureTree
          ? undefined
          : {
              label: "Align",
              onClicked: () => {
                if (!task.iElementId) return;

                const appState = appStore.getState();
                const taskElement = selectIElement(task.iElementId)(appState);
                if (!taskElement) return;

                const laserScanSection = selectAncestor(
                  taskElement,
                  isIElementSectionDataSession,
                )(appState);
                if (!laserScanSection) return;

                const activeArea = selectAreaFor(laserScanSection)(appState);
                if (!activeArea) return;

                const pointCloud = selectChildDepthFirst(
                  laserScanSection,
                  isIElementPointCloudStream,
                )(appState);

                const dataSession = selectAncestor(
                  pointCloud,
                  isIElementSectionDataSession,
                )(appState);

                if (!pointCloud || !dataSession) return;

                openAlignmentWizard({
                  elementIdToAlign: dataSession.id,
                  dispatch: appStore.dispatch,
                });
              },
            },
    });
  },

  failed({ task, openToast, appStore }): Promise<void> {
    assert(
      task.type === ProgressApiSupportedTaskTypes.pointCloudLazToPotree ||
        task.type === ProgressApiSupportedTaskTypes.pointCloudToPotree,
    );

    const element = task.iElementId
      ? selectIElement(task.iElementId)(appStore.getState())
      : undefined;

    const { taskName, errorCode } = getErrorCodeAndTaskName(
      task,
      element?.name,
    );

    openToast({
      title: "File Conversion Failed",
      message: taskErrorToUserMessage(taskName, errorCode),
      variant: "error",
    });
    return Promise.resolve();
  },
};

/**
 * Map to pair each background task with the actions needed when the state changes
 * to completed or failed.
 * In future we could move these function to a specific file for each task and just reference
 * them here
 */
const TASK_ACTIONS: Partial<Record<BackgroundTaskTypes, Partial<TaskActions>>> =
  {
    // Actions for the FileUpload task state changes
    FileUpload: {
      completed({ task, openToast }) {
        assert(task.type === "FileUpload");

        if (task.silent) return Promise.resolve();

        // Custom toast for tasks that have uploaded a file for an ImgSheet
        if (isImageFileUploadTask(task)) {
          const sheetName = task.metadata.elementName ? (
            <>
              <TranslateVar name="sheetName">
                {task.metadata.elementName}
              </TranslateVar>{" "}
            </>
          ) : (
            "ImageSheet "
          );

          openToast({
            title: (
              <>
                <TranslateVar name="sheet-name">{sheetName}</TranslateVar>{" "}
                uploaded successfully
              </>
            ),
            message: "",
          });
          return Promise.resolve();
        }

        // Don't suggest to the user that the window can be closed, while other uploads are still running
        const areOtherUploadTasksRunning = selectActiveBackgroundTasks(
          store.getState(),
        ).some(
          (otherTask) => isFileUploadTask(task) && task.id !== otherTask.id,
        );

        if (!areOtherUploadTasksRunning) {
          openToast({
            title: "Upload successful, now processing.",
            message: "You can close the browser during this process",
            variant: "success",
          });
        }
        return Promise.resolve();
      },

      failed({ task, openToast }) {
        assert(task.type === "FileUpload");

        if (task.silent) return Promise.resolve();

        openToast({
          title: "File Upload",
          message: `File ${task.metadata.filename} upload failed`,
          variant: "error",
        });
        return Promise.resolve();
      },
    },
    // Actions for the MultiFileUpload task state changes
    MultiFileUpload: {
      completed({ task, openToast }) {
        assert(isMultiFileUploadTask(task));

        if (task.silent) return Promise.resolve();
        const fileMessage =
          task.metadata.filenames.length === 1
            ? task.metadata.filenames[0]
            : `${task.metadata.filenames.length} files`;

        openToast({
          title: `${fileMessage} uploaded successfully`,
          message: "",
        });
        return Promise.resolve();
      },

      failed({ task, openToast }) {
        assert(isMultiFileUploadTask(task));

        if (task.silent) return Promise.resolve();
        const fileMessage =
          task.metadata.filenames.length === 1
            ? task.metadata.filenames[0]
            : `${task.metadata.filenames.length} files`;

        openToast({
          title: "Files Upload",
          message: `Failed to upload ${fileMessage}`,
          variant: "error",
        });
        return Promise.resolve();
      },
    },
    // Actions for the PointCloudLazToPotree task state changes
    PointCloudToPotree: pcConversionAction,
    // TODO: Remove after backend update: https://faro01.atlassian.net/browse/SWEB-1980
    PointCloudLazToPotree: pcConversionAction,

    PointCloudExport: {
      completed({ task, openToast }) {
        if (!isPointCloudExportTask(task) || !task.metadata.downloadUrl) {
          return Promise.resolve();
        }

        const pcName = getFileNameFromUrl(task.metadata.downloadUrl);

        openToast({
          title: (
            <>
              <TranslateVar name="pointcloudName">{pcName}</TranslateVar> is now
              available for download
            </>
          ),
          message: "Check your email or the Cloud activity.",
          variant: "success",
        });
        return Promise.resolve();
      },
      failed({ task, openToast }) {
        assert(task.type === ProgressApiSupportedTaskTypes.pointCloudExport);
        openToast({
          title: "Point cloud export",
          message: "Export failed",
          variant: "error",
        });
        return Promise.resolve();
      },
    },
    BimModelImport: {
      completed({ task, openToast, appStore }) {
        assert(task.type === ProgressApiSupportedTaskTypes.bimModelImport);

        const element = task.iElementId
          ? selectIElement(task.iElementId)(appStore.getState())
          : undefined;

        const message = (
          <>
            The import of{" "}
            {element?.name ? (
              <TranslateVar name="modelName">{element.name}</TranslateVar>
            ) : (
              "the 3D Model"
            )}{" "}
            was completed successfully
          </>
        );

        openToast({
          title: "Import Successful",
          message,
          variant: "success",
          persist: true,
        });
        return Promise.resolve();
      },

      async failed({ task, openToast, appStore, projectApi }) {
        assert(task.type === ProgressApiSupportedTaskTypes.bimModelImport);

        const element = task.iElementId
          ? selectIElement(task.iElementId)(appStore.getState())
          : undefined;

        if (element) {
          await appStore.dispatch(
            updateProject({
              projectApi,
              iElementQuery: {
                ancestorIds: [element.id],
              },
            }),
          );

          const newElement = selectIElement(element.id)(appStore.getState());

          const bimModelStream = selectChildDepthFirst(
            newElement,
            isIElementModel3dStream,
          )(appStore.getState());

          if (bimModelStream) {
            appStore.dispatch(setActiveCad(bimModelStream.id));
          }
        }

        const { taskName, errorCode } = getErrorCodeAndTaskName(
          task,
          element?.name,
        );

        openToast({
          title: "File Conversion Failed",
          message: taskErrorToUserMessage(taskName, errorCode),
          variant: "error",
        });
      },
    },

    // Actions for multi cloud/data set registration
    // The messages will be changed in future design iterations
    RegisterMultiCloudDataSet: {
      completed({ openToast }) {
        openToast({
          title: "Data Set Registration Completed",
          message: "The point clouds in the data set have been aligned.",
          variant: "success",
        });
        return Promise.resolve();
      },

      failed({ openToast }) {
        openToast({
          title: "Data Set Registration Failed",
          message: "There was an error while trying to align the point clouds.",
          variant: "error",
        });
        return Promise.resolve();
      },
    },
  };

type HandledTask = Pick<BackgroundTask, "id" | "type" | "state">;

/**
 * @returns A container that will track tasks and execute action when their state change
 *
 * For now what action to do for each task is statically encoded in this component, so each task type
 * will always have the same action.
 * This is the simplest design and is ok for our current needs.
 * If we will ever need custom actions for the same task then we can refactor this component to allow
 * components to register actions using a Context to track the actions and a custom hook
 */
export function BackgroundTaskNotifier(): JSX.Element | null {
  const { openToast } = useToast();
  const { createDialog } = useDialog();
  const appStore = useAppStore();

  const projectApi = useCurrentProjectApiClient();

  const backgroundTasks = useAppSelector(selectBackgroundTasks(), isEqual);

  /** Keeping track of all the tasks that have already been checked */
  const [handledTasks, setHandledTasks] = useState<HandledTask[]>(
    backgroundTasks.map((task) => ({
      id: task.id,
      type: task.type,
      state: task.state,
    })),
  );

  useEffect(() => {
    for (const backgroundTask of backgroundTasks) {
      if (
        backgroundTask.state !== BackgroundTaskState.succeeded &&
        backgroundTask.state !== BackgroundTaskState.failed
      ) {
        continue;
      }

      // Avoid showing a notification for the same task multiple times or
      // for a task that has not changed its state
      const handledTask = handledTasks.find(
        (task) => task.id === backgroundTask.id,
      );
      if (!handledTask || handledTask.state === backgroundTask.state) {
        continue;
      }

      const action =
        backgroundTask.state === BackgroundTaskState.succeeded
          ? TASK_ACTIONS[backgroundTask.type]?.completed
          : TASK_ACTIONS[backgroundTask.type]?.failed;
      if (!action) continue;

      action({
        appStore,
        createDialog,
        openToast,
        task: backgroundTask,
        projectApi,
      });
    }

    setHandledTasks(
      backgroundTasks.map((task) => ({
        id: task.id,
        type: task.type,
        state: task.state,
      })),
    );

    // Not adding handledTasks as dep because there is no need to run this effect in case the other effect updates the state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundTasks]);

  return null;
}
